<template>
  <div class="flex min-h-screen bg-gray-100 dark:bg-gray-900">
    <!-- Sidebar -->
    <aside
      :class="[
        'fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-all duration-300 ease-in-out flex flex-col',
        isCollapsed ? 'w-16' : 'w-64',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      ]"
    >
      <!-- Logo/Brand -->
      <div class="flex  px-4 py-4 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <template v-if="isCollapsed">
          <div class="w-8 h-8 rounded-lg flex items-center justify-center">
            <DiligintLogoSm class="text-diligint  w-6 h-6" />
          </div>
        </template>
        <template v-else>
            <div class="flex w-36">
               <DiligintLogo class="text-diligint px-3" />
            </div>
        </template>
      </div>

      <!-- Navigation Menu - REMOVED Participants -->
      <nav class="flex-1 px-2 py-6 space-y-1 overflow-y-auto min-h-0">
        <!-- Main Navigation -->
        <div class="space-y-1">
          <!-- Dashboard -->
          <router-link
            to="/dashboard"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path === '/dashboard' }"
            :title="isCollapsed ? 'Dashboard' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Dashboard</span>
            </template>
          </router-link>

          <!-- Projects -->
          <router-link
            to="/projects"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/projects') }"
            :title="isCollapsed ? 'Projects' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Projects</span>
              <template v-if="activeProjectsCount > 0">
                <span class="ml-auto bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-300 text-xs px-2 py-1 rounded-full">
                  {{ activeProjectsCount }}
                </span>
              </template>
            </template>
          </router-link>
        </div>

        <!-- Research Sessions Section -->
        <div class="space-y-1 pt-4">
          <template v-if="!isCollapsed">
            <div class="flex items-center justify-between px-3 py-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Research</span>
              <button 
                @click="startNewResearch"
                class="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition"
                title="New Research Session"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                </svg>
              </button>
            </div>
          </template>

          <!-- All Research Sessions -->
          <router-link
            to="/research"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/research') && !$route.path.includes('/projects/') }"
            :title="isCollapsed ? 'All Research Sessions' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">All Sessions</span>
            </template>
          </router-link>

          <!-- Active Research -->
          <router-link
            to="/research/active"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path === '/research/active' }"
            :title="isCollapsed ? 'Active Research' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Active Research</span>
              <template v-if="activeResearchCount > 0">
                <span class="ml-auto bg-green-100 dark:bg-green-800 text-green-600 dark:text-green-300 text-xs px-2 py-1 rounded-full">
                  {{ activeResearchCount }}
                </span>
              </template>
            </template>
          </router-link>
        </div>

        <!-- Documents & Data Section -->
        <div class="space-y-1 pt-4">
          <template v-if="!isCollapsed">
            <div class="px-3 py-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data & Files</span>
            </div>
          </template>

          <!-- Documents -->
          <router-link
            to="/documents"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/documents') }"
            :title="isCollapsed ? 'Documents' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Documents</span>
            </template>
          </router-link>

          <!-- Data Sources -->
          <router-link
            to="/data-sources"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/data-sources') }"
            :title="isCollapsed ? 'Data Sources' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Data Sources</span>
            </template>
          </router-link>

          <!-- ChatGPT -->
          <router-link
            to="/chat"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/chat') }"
            :title="isCollapsed ? 'ChatGPT' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">ChatGPT</span>
            </template>
          </router-link>
        </div>

        <!-- Analysis & Reports Section -->
        <div class="space-y-1 pt-4">
          <template v-if="!isCollapsed">
            <div class="px-3 py-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Analysis</span>
            </div>
          </template>

          <!-- Analyze -->
          <router-link
            to="/analyze"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/analyze') }"
            :title="isCollapsed ? 'Analyze' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Analyze</span>
            </template>
          </router-link>

          <!-- Reports -->
          <router-link
            to="/reports"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/reports') }"
            :title="isCollapsed ? 'Reports' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Reports</span>
            </template>
          </router-link>

          <!-- Insights -->
          <router-link
            to="/insights"
            @click="handleSidebarLinkClick"
            class="flex items-center gap-3 px-3 py-2 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition group"
            :class="{ 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400': $route.path.startsWith('/insights') }"
            :title="isCollapsed ? 'Insights' : ''"
          >
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
            </svg>
            <template v-if="!isCollapsed">
              <span class="font-medium">Insights</span>
            </template>
          </router-link>
        </div>

        <!-- Recent Activity Section (Only when expanded) -->
        <template v-if="!isCollapsed && recentSessions.length > 0">
          <div class="space-y-1 pt-4">
            <div class="px-3 py-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Recent</span>
            </div>
            
            <template v-for="session in recentSessions.slice(0, 3)" :key="session.id">
              <button
                @click="goToResearchSession(session)"
                class="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition group w-full text-left"
              >
                <div class="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full group-hover:bg-blue-500 flex-shrink-0"></div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{{ session.entityName || session.searchQuery || 'Untitled Session' }}</p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatDate(session.updatedAt) }}</p>
                </div>
              </button>
            </template>
          </div>
        </template>
      </nav>

      <!-- Bottom Section -->
      <div class="border-t border-gray-200 dark:border-gray-800 p-2 flex-shrink-0">
        <!-- New Research Button -->
        <template v-if="isCollapsed">
          <button
            @click="startNewResearch"
            class="w-full flex items-center justify-center p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition mb-2"
            title="New Research"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </template>
        <template v-else>
          <button
            @click="startNewResearch"
            class="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition mb-4"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
            New Research
          </button>
        </template>

        <!-- User & Settings -->
        <div class="space-y-1">
          <template v-if="isCollapsed">
            <button
              @click="userDropdownOpen = !userDropdownOpen"
              class="relative w-full flex items-center justify-center p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              title="User Menu"
            >
              <div class="w-6 h-6 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                <span class="text-xs font-medium text-blue-600 dark:text-blue-300">{{ userInitials }}</span>
              </div>
            </button>
          </template>
          <template v-else>
            <router-link
              to="/settings"
              @click="handleSidebarLinkClick"
              class="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span class="text-sm">Settings</span>
            </router-link>

            <button
              @click="logout"
              class="flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition w-full text-left"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
              <span class="text-sm">Sign Out</span>
            </button>
          </template>
        </div>
      </div>
    </aside>

    <!-- Mobile overlay -->
    <template v-if="sidebarOpen">
      <div 
        class="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
        @click="sidebarOpen = false"
      ></div>
    </template>

    <!-- Main Content Wrapper -->
    <div class="flex-1 flex flex-col min-w-0">
      <!-- Top Navbar -->
      <header
        :class="[
          'flex items-center justify-between bg-white dark:bg-gray-800 shadow px-6 h-16 fixed top-0 z-20 transition-all duration-300 ease-in-out',
          isCollapsed ? 'left-0 lg:left-16' : 'left-0 lg:left-64',
          'right-0'
        ]"
      >
        <!-- Left: Menu Button & Page Title -->
        <div class="flex items-center gap-4">
          <!-- Mobile Menu Button -->
          <button
            @click="sidebarOpen = !sidebarOpen"
            class="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>

          <!-- Desktop Collapse Button -->
          <button
            @click="isCollapsed = !isCollapsed"
            class="hidden lg:block p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="isCollapsed ? 'M9 5l7 7-7 7' : 'M15 19l-7-7 7-7'"/>
            </svg>
          </button>

          <h1 class="text-xl font-semibold text-gray-900 dark:text-white">{{ headerTitle }}</h1>
        </div>

        <!-- Right: Actions -->
        <div class="flex items-center gap-3">
          <!-- Notifications -->
          <button class="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            <BellIcon class="h-5 w-5 text-gray-500 dark:text-gray-400" />
            <template v-if="hasNotifications">
              <span class="absolute -top-1 -right-1 block h-2 w-2 rounded-full bg-red-500"></span>
            </template>
          </button>

          <!-- User Dropdown -->
          <div class="relative">
            <button
              @click="userDropdownOpen = !userDropdownOpen"
              class="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div class="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
                <span class="text-sm font-medium text-blue-600 dark:text-blue-300">{{ userInitials }}</span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>

            <template v-if="userDropdownOpen">
              <div class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                <button
                  @click="logout"
                  class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Sign Out
                </button>
              </div>
            </template>
          </div>

          <!-- Theme Toggle -->
          <button
            @click="toggleTheme"
            class="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            :title="`Theme: ${theme.charAt(0).toUpperCase() + theme.slice(1)}`"
          >
            <component :is="icons[theme]" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
      </header>

      <!-- Main Content Area -->
      <main
        :class="[
          'flex-1 pt-16 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-all duration-300 ease-in-out',
          isCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        ]"
      >
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useResearchStore } from '@/stores/research'
import { useSessionStore } from '@/stores/session'
import { BellIcon, ChevronDownIcon, SunIcon, MoonIcon, ComputerDesktopIcon } from '@heroicons/vue/24/outline'
import DiligintLogoSm from '@/assets/DiligintLogoSm.vue'
import DiligintLogo from '@/assets/DiligintLogo.vue'
const sidebarOpen = ref(false)
const userDropdownOpen = ref(false)
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const researchStore = useResearchStore()
const sessionStore = useSessionStore()

const headerTitle = computed(() => {
  switch (route.name) {
    case 'Dashboard':
      return 'Dashboard'
    case 'Projects':
      return 'Projects'
    case 'Participants':
      return 'Participants'
    case 'Chat':
      return 'ChatGPT'
    case 'Research':
      return 'Research'
    case 'Analyze':
      return 'Analyze'
    case 'Report':
      return 'Report'
    default:
      return 'Welcome'
  }
})

const theme = ref('auto')
const icons = {
  light: SunIcon,
  dark: MoonIcon,
  auto: ComputerDesktopIcon,
}
const nextTheme = {
  auto: 'light',
  light: 'dark',
  dark: 'auto',
}

function setTheme(newTheme) {
  theme.value = newTheme
  if (newTheme === 'auto') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    document.documentElement.classList.toggle('dark', prefersDark)
  } else if (newTheme === 'dark') {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
  localStorage.setItem('theme', newTheme)
}

function toggleTheme() {
  setTheme(nextTheme[theme.value])
}

onMounted(() => {
  const saved = localStorage.getItem('theme') || 'auto'
  setTheme(saved)
})

function logout() {
  authStore.logout()
  userDropdownOpen.value = false
  sidebarOpen.value = false
  router.push('/login')
}

const isCollapsed = ref(false)

// Auto-collapse sidebar for research workflows with AgentChatSidebar
/* watch(
  () => route.path,
  (newPath) => {
    // Auto-collapse when AgentChatSidebar is present (research workflows)
    if (
      newPath.startsWith('/projects/') &&
      (newPath.includes('/research') || 
       newPath.includes('/data-collection') || 
        newPath.includes('/data-overview') || 
       newPath.includes('/analysis') ||
       newPath.includes('/risk-analysis'))
    ) {
      isCollapsed.value = true
    } else {
      isCollapsed.value = false
    }
  },
  { immediate: true }
) */

// Auto-hide sidebar on mobile after navigation
function handleSidebarLinkClick() {
  if (window.innerWidth < 1024) {
    sidebarOpen.value = false
  }
}

// Computed properties for dynamic counts
const activeProjectsCount = computed(() => {
  return 0
})

const activeResearchCount = computed(() => {
  return researchStore.researchJobs?.size || 0
})

const recentSessions = computed(() => {
  return researchStore.searchHistory?.slice(0, 5) || []
})

const recentIdentitySessions = computed(() => {
  return sessionStore.sessions?.slice(0, 3) || []
})

const hasNotifications = computed(() => {
  return false
})

const userInitials = computed(() => {
  const user = authStore.user
  if (!user) return 'U'
  return (user.firstName?.[0] || '') + (user.lastName?.[0] || '')
})

function startNewResearch() {
  router.push('/projects?action=new')
  handleSidebarLinkClick()
}

function formatDate(date) {
  if (!date) return ''
  const d = new Date(date)
  const now = new Date()
  const diffTime = Math.abs(now - d)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return d.toLocaleDateString()
}

function goToResearchSession(session) {
  if (session.projectId && session.id) {
    router.push(`/projects/${session.projectId}/research?sessionId=${session.id}`)
    handleSidebarLinkClick()
  }
}

function goToIdentitySession(session) {
  if (session.projectId && session._id) {
    router.push(`/projects/${session.projectId}/research?sessionId=${session._id}`)
    handleSidebarLinkClick()
  }
}

// Expose sidebar control for parent components
defineExpose({
  toggleSidebar: () => sidebarOpen.value = !sidebarOpen.value,
  sidebarOpen
})
</script>

<style>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>